<template>
  <div class="p-2 flex items-center justify-center">
    <div class="container max-w-screen-lg mx-auto">
      <div>
        <div class="bg-white rounded shadow-lg p-4 px-4 md:p-8">
          <div class="grid gap-4 gap-y-2 text-sm grid-cols-1 lg:grid-cols-3">
            <div class="text-left mb-2">
              <p class="mb-2 font-medium text-lg text-gray-800">
                {{ descriptionTitle }}
              </p>

              <p class="text-gray-500">
                Campos com <span class="text-red-500">*</span> são obrigatórios
              </p>

              <div
                v-if="isBatchMode"
                class="mt-6 rounded-md border border-green-200 bg-green-50 p-4"
              >
                <div class="flex items-start gap-3">
                  <PhFileXls class="w-8 h-8 text-green-600 mt-0.5" />

                  <div class="flex-1">
                    <p class="font-semibold text-green-800">
                      Planilha carregada com sucesso
                    </p>

                    <p class="text-sm text-green-700 mt-1 break-all">
                      {{ selectedFileName }}
                    </p>

                    <p class="text-sm text-green-700 mt-1">
                      {{ importedUsersCount }} colaborador(es) pronto(s) para importação.
                    </p>

                    <p class="text-xs text-green-700 mt-2">
                      Enquanto a planilha estiver carregada, os campos manuais serão ignorados no envio.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div class="lg:col-span-2">
              <div class="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
                <!-- nome -->
                <div class="md:col-span-3">
                  <BaseInput
                    v-model="state.name"
                    name="name"
                    :info="'Nome do colaborador'"
                    label="Nome"
                    :required-field="true"
                    type="text"
                  >
                    <template #append>
                      <CleanInputButton
                        @click="state.name = null"
                      />
                    </template>
                  </BaseInput>

                  <span name="name" class="text-red-600 text-sm" />
                </div>

                <!-- CPF -->
                <div class="md:col-span-2">
                  <BaseInput
                    v-model="state.cpf"
                    name="cpf"
                    v-mask="'###.###.###-##'"
                    :info="'Informe o CPF'"
                    label="CPF"
                    type="text"
                  >
                    <template #append>
                      <CleanInputButton
                        @click="state.cpf = null"
                      />
                    </template>
                  </BaseInput>
                </div>

                <!-- email -->
                <div class="md:col-span-3">
                  <BaseInput
                    v-model="state.email"
                    name="email"
                    :info="'Usuário de acesso'"
                    label="Usuário de acesso"
                    :required-field="true"
                  >
                    <template #append>
                      <CleanInputButton
                        @click="state.email = null"
                      />
                    </template>
                  </BaseInput>

                  <span name="email" class="text-red-600 text-sm" />
                </div>

                <!-- função -->
                <div class="md:col-span-2">
                  <div class="md:col-span-2 mt-1.5">
                    <label
                      class="text-left flex pl-1 text-xs text-[#2b3038]"
                      for="relatosForm.empresa"
                    >
                      Cargo
                    </label>
                    <div class="h-10 flex rounded items-center text-center">
                      <SimpleSelect
                        class="px-1 py-2 text-center uppercase appearance-none outline-none text-gray-800 w-full bg-transparent"
                        :data="role"
                        :disable-keyboard="true"
                        @selected-data="
                          (val) => (state.role = val.value)
                        "
                      />
                    </div>
                  </div>
                </div>

                <!-- upload excel -->
                <div class="md:col-span-5 flex h-10" v-if="isCreate == true">
                  <label
                    class="cursor-pointer bg-gray-100 hover:bg-gray-300 transition-all min-w-[10rem] pt-2.5 h-10 rounded-l-md rounded-r-none"
                  >
                    <div class="flex my-auto h-full">
                      <div class="w-full my-auto flex justify-center h-full">
                        <component
                          :is="isBatchMode ? PhUserCircleCheck : PhUser"
                          class="w-5 h-5 mr-1"
                          :class="[
                            isBatchMode ? 'text-green-500' : 'text-gray-500'
                          ]"
                        />

                        Cadastrar em lote
                      </div>
                    </div>
                    <input
                      ref="fileInputRef"
                      type="file"
                      class="hidden"
                      accept=".xlsx,.xls,.csv"
                      @change="handleFileChange"
                    />
                  </label>
                  <div class="border rounded w-full h-10 rounded-r-md rounded-l-none flex items-center justify-between overflow-hidden">
                    <input
                      type="text"
                      style="padding-left: 10px !important"
                      class="w-full h-full outline-none"
                      :value="selectedFileName"
                      readonly
                    />

                    <button
                      v-if="isBatchMode"
                      type="button"
                      class="px-3 h-full border-l bg-red-50 hover:bg-red-100 text-red-600 transition-all"
                      @click="clearImportedFile"
                    >
                      Limpar
                    </button>
                  </div>
                </div>

                <div class="md:col-span-5">
                  <small class="text-gray-500">
                    Estrutura esperada da planilha: coluna A = NOME, coluna B = CPF, coluna C = CARGO. A leitura começa na linha 2.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="p-2 mt-1 mb-1" />

        <div>
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import * as XLSX from 'xlsx'
import CleanInputButton from '../components/CleanInputButton.vue'
import BaseInput from '@/components/Inputs/BaseInput.vue'
import { dataFighters } from '../store'
import { showToast as toast } from '@/helpers/UseToasts'
import { PhUser, PhUserCircleCheck, PhFileXls } from '@phosphor-icons/vue'
import { ref, defineProps, computed } from 'vue'
import SimpleSelect from '@/components/Select/SimpleSelect.vue'

defineProps({
  descriptionTitle: String,
  isCreate: Boolean
})

const store = dataFighters
const state = dataFighters.state
const fileInputRef = ref(null)
const role = [
  {
    name: 'ALUNO',
    value: 'student'
  },
  {
    name: 'SUPERVISOR',
    value: 'manager'
  }
]

const selectedFileName = computed(() => store.state.importFileName)
const importedUsersCount = computed(() => store.state.importUsers.length)
const isBatchMode = computed(() => importedUsersCount.value > 0)

async function handleFileChange (event) {
  const file = event.target.files?.[0]

  if (!file) {
    return
  }

  const allowedExtensions = ['xlsx', 'xls', 'csv']
  const fileExtension = file.name.split('.').pop()?.toLowerCase()

  if (!allowedExtensions.includes(fileExtension)) {
    clearImportedFile()
    return toast(
      'Erro',
      'Tipo de arquivo não é válido. Envie uma planilha .xlsx, .xls ou .csv.',
      'error'
    )
  }

  try {
    const importedUsers = await parseSpreadsheet(file)

    if (!importedUsers.length) {
      throw new Error('Nenhum colaborador válido foi encontrado na planilha.')
    }

    store.commit('SET_IMPORT_USERS', importedUsers)
    store.commit('SET_IMPORT_FILE_NAME', file.name)
    store.commit('SET_IMPORT_ERRORS', [])

    toast(
      'Sucesso',
      `${importedUsers.length} colaborador(es) carregado(s) para importação.`,
      'success'
    )
  } catch (error) {
    clearImportedFile()

    return toast(
      'Erro',
      error.message || 'Não foi possível ler a planilha informada.',
      'error'
    )
  }
}

function clearImportedFile () {
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }

  store.commit('CLEAR_IMPORT_DATA')
}

async function parseSpreadsheet (file) {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const firstSheetName = workbook.SheetNames[0]
  const worksheet = workbook.Sheets[firstSheetName]

  const rows = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: false
  })

  if (!rows.length || rows.length === 1) {
    throw new Error('A planilha precisa conter ao menos uma linha de colaborador a partir da linha 2.')
  }

  const importedUsers = []
  const errors = []

  rows.slice(1).forEach((row, index) => {
    const lineNumber = index + 2
    const [nameRaw, cpfRaw, roleRaw] = row

    const name = normalizeText(nameRaw)
    const cpf = formatCpf(cpfRaw)
    const role = mapRole(roleRaw)

    if (!name && !cpf && !normalizeText(roleRaw)) {
      return
    }

    if (!name) {
      errors.push(`Linha ${lineNumber}: informe o NOME.`)
      return
    }

    if (!cpf) {
      errors.push(`Linha ${lineNumber}: informe um CPF válido.`)
      return
    }

    if (!role) {
      errors.push(`Linha ${lineNumber}: o CARGO deve ser ALUNO ou SUPERVISOR.`)
      return
    }

    importedUsers.push({
      name,
      cpf,
      role
    })
  })

  if (errors.length) {
    throw new Error(errors.slice(0, 5).join(' | '))
  }

  return importedUsers
}

function normalizeText (value) {
  return String(value ?? '').trim()
}

function normalizeRoleText (value) {
  return normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function mapRole (value) {
  const normalizedRole = normalizeRoleText(value)

  const roleMap = {
    aluno: 'student',
    estudantes: 'student',
    estudante: 'student',
    student: 'student',
    supervisor: 'manager',
    supervisora: 'manager',
    manager: 'manager',
    gestor: 'manager',
    gestora: 'manager'
  }

  return roleMap[normalizedRole] || null
}

function formatCpf (value) {
  const digits = String(value ?? '').replace(/\D/g, '')

  if (!digits) {
    return null
  }

  const normalizedDigits = digits.length < 11
    ? digits.padStart(11, '0')
    : digits.slice(0, 11)

  if (normalizedDigits.length !== 11) {
    return null
  }

  return normalizedDigits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}
</script>
